import { Select } from 'antd';
import { Plus } from 'lucide-react';

import SlackIcon from '@/assets/icons/SlackIcon';
import { Button } from '@/components/shadcn/ui/button';
import { Input } from '@/components/shadcn/ui/input';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/shadcn/ui/toggle-group';
import { cn } from '@/lib/cn';
import { WORKFLOW_CHANNEL_TYPE } from '@/types/workflowConst';

import type { NotificationChannelProps } from './types';
import { useSlackWebhook } from './use-slack-webhook';

/**
 * Notification Channel 선택 컴포넌트 (Controlled)
 *
 * Slack/Email 채널 선택 UI를 제공하는 공용 컴포넌트
 * Legacy workflow (Periodic, Subscription, Realtime)에서 사용
 */
export function NotificationChannel({
  value,
  onChange,
  workspaceId,
  hideEmailOption = false,
  className,
}: NotificationChannelProps) {
  const { slackChannels, connectSlack } = useSlackWebhook({ workspaceId });

  // 현재 채널 타입 (기본값: SLACK)
  const channelType = value?.type ?? WORKFLOW_CHANNEL_TYPE.SLACK;

  // 채널 타입 변경 핸들러
  const handleChannelTypeChange = (type: string) => {
    if (!type) return;

    if (type === WORKFLOW_CHANNEL_TYPE.SLACK) {
      // Slack으로 변경 시 channel을 undefined로 리셋
      onChange(undefined);
    } else {
      // Email으로 변경 시 빈 email 채널 생성
      onChange({ type: WORKFLOW_CHANNEL_TYPE.MAIL, mail_address: '' });
    }
  };

  // Slack 채널 선택 핸들러
  const handleSlackChannelChange = (webhookId: string) => {
    onChange({
      type: WORKFLOW_CHANNEL_TYPE.SLACK,
      slack_webhook_id: webhookId,
    });
  };

  // Email 입력 핸들러
  const handleEmailChange = (email: string) => {
    onChange({
      type: WORKFLOW_CHANNEL_TYPE.MAIL,
      mail_address: email,
    });
  };

  return (
    <div className={cn('flex flex-1 flex-col gap-3', className)}>
      {/* Slack/Email 토글 */}
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={channelType}
        onValueChange={handleChannelTypeChange}
      >
        <ToggleGroupItem value={WORKFLOW_CHANNEL_TYPE.SLACK}>Slack</ToggleGroupItem>
        {!hideEmailOption && (
          <ToggleGroupItem value={WORKFLOW_CHANNEL_TYPE.MAIL}>Email</ToggleGroupItem>
        )}
      </ToggleGroup>

      {/* Slack 채널 선택 UI */}
      {channelType === WORKFLOW_CHANNEL_TYPE.SLACK && (
        <div className="flex items-center gap-3">
          <SlackIcon size="24px" />
          {slackChannels.length > 0 ? (
            <>
              <Select
                placeholder="Select slack channel..."
                value={
                  value?.type === WORKFLOW_CHANNEL_TYPE.SLACK
                    ? value.slack_webhook_id
                    : undefined
                }
                options={slackChannels.map((channel) => ({
                  value: channel.alias,
                  label: `${channel.team_name} / ${channel.channel_name}`,
                }))}
                onChange={handleSlackChannelChange}
                virtual={false}
                dropdownStyle={{ width: 'auto' }}
                className="min-w-[180px]"
              />
              <Button variant="outline" size="sm" onClick={connectSlack}>
                <Plus className="mr-1 size-4" />
                Add
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={connectSlack}>
              <Plus className="mr-1 size-4" />
              Connect
            </Button>
          )}
        </div>
      )}

      {/* Email 입력 UI */}
      {channelType === WORKFLOW_CHANNEL_TYPE.MAIL && (
        <Input
          type="email"
          placeholder="Enter email address"
          value={
            value?.type === WORKFLOW_CHANNEL_TYPE.MAIL ? value.mail_address : ''
          }
          onChange={(e) => handleEmailChange(e.target.value)}
          className="max-w-[300px]"
        />
      )}
    </div>
  );
}
